import { Project } from '../../../types/project';
import { Task } from '../../../types/task';
import { format, isToday, isYesterday, parseISO, subDays, differenceInDays } from 'date-fns';
import { useState, useEffect } from 'react';
import { ProjectService } from '../../../services/projectService';
import { TaskService } from '../../../services/taskService';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export const useUserStats = () => {
  const { t } = useTranslation(['v0/common', 'v0/me']);
  const toast = useToast();
  
  // 状态
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const today = new Date();

  // 基础统计数据
  const totalProjects = projects.length;
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.done).length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const urgentTasks = tasks.filter(t => t.urgent && !t.done).length;
  const vitalTasks = tasks.filter(t => t.vital && !t.done).length;

  // 最近的任务
  const recentTasks = [...tasks]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);

  // 优先级任务
  const priorityTasks = tasks
    .filter(t => (t.urgent || t.vital) && !t.done)
    .slice(0, 3);

  // 日期相关计算
  const getActivityDates = () => {
    const lastWeek = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(today, 6 - i);
      return format(date, 'yyyy-MM-dd');
    });

    return lastWeek;
  };

  // 活动热图数据 - 基于真实任务数据
  const getActivityData = () => {
    const dates = getActivityDates();

    // 统计每天完成的任务数量
    const dailyCompletions = dates.map(date => {
      const completedOnDate = tasks.filter(task => {
        if (!task.done) return false;
        const taskUpdateDate = task.updatedAt ? format(new Date(task.updatedAt), 'yyyy-MM-dd') : null;
        return taskUpdateDate === date;
      }).length;

      return {
        date,
        count: completedOnDate
      };
    });

    return dailyCompletions;
  };

  // 每日趋势数据 - 基于真实任务
  const getProductivityData = () => {
    // 过去一周创建的任务
    const tasksCreatedLastWeek = tasks.filter(task => {
      const createDate = new Date(task.createdAt);
      return differenceInDays(today, createDate) <= 7;
    }).length;

    // 过去一周完成的任务
    const tasksCompletedLastWeek = tasks.filter(task => {
      if (!task.done) return false;
      const updateDate = new Date(task.updatedAt);
      return differenceInDays(today, updateDate) <= 7;
    }).length;

    // 连续完成任务的天数（简化计算）
    let streakDays = 0;
    for (let i = 0; i < 7; i++) {
      const checkDate = subDays(today, i);
      const formattedDate = format(checkDate, 'yyyy-MM-dd');

      const completedOnDate = tasks.some(task => {
        if (!task.done) return false;
        const taskUpdateDate = task.updatedAt ? format(new Date(task.updatedAt), 'yyyy-MM-dd') : null;
        return taskUpdateDate === formattedDate;
      });

      if (completedOnDate) {
        streakDays++;
      } else if (i === 0) {
        // 如果今天没有完成任务，但还是计算昨天开始的连续天数
        continue;
      } else {
        // 一旦断了，就停止计算
        break;
      }
    }

    return {
      tasksCreated: tasksCreatedLastWeek,
      tasksCompleted: tasksCompletedLastWeek,
      streak: streakDays
    };
  };

  // 获取最高效的日期和时间段（基于真实数据分析）
  const getProductivityInsights = () => {
    // 按照一周七天分组统计完成的任务数
    const dayOfWeekStats = [0, 0, 0, 0, 0, 0, 0]; // 周日到周六

    tasks.forEach(task => {
      if (task.done && task.updatedAt) {
        const updateDate = new Date(task.updatedAt);
        const dayOfWeek = updateDate.getDay(); // 0 - 周日, 1 - 周一, ...
        dayOfWeekStats[dayOfWeek]++;
      }
    });

    // 找出完成任务最多的一天
    let mostProductiveDayIndex = 0;
    let maxTasks = 0;

    dayOfWeekStats.forEach((count, index) => {
      if (count > maxTasks) {
        maxTasks = count;
        mostProductiveDayIndex = index;
      }
    });

    // 动态获取翻译后的星期名称
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const mostProductiveDayKey = `common:time.daysOfWeek.${dayKeys[mostProductiveDayIndex]}`;
    const mostProductiveDay = t(mostProductiveDayKey, dayKeys[mostProductiveDayIndex]); // Fallback to key name

    return {
      day: mostProductiveDay,
      taskCount: maxTasks
    };
  };

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      try {
        // 并行加载数据
        const [projectsData, tasksData] = await Promise.all([
          ProjectService.getProjects(),
          TaskService.getTasks()
        ]);

        setProjects(projectsData);
        setTasks(tasksData);
      } catch (error) {
        console.error('Failed to load data:', error);
        toast({
          title: t('v0/common:errors.loadingDataFailed', '加载数据失败'),
          description: error instanceof Error ? error.message : t('v0/common:errors.unknown', '未知错误'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadData();
  }, [toast, t]);

  return {
    // 基础数据
    projects,
    tasks,
    today,
    
    // 统计数据
    totalProjects,
    totalTasks,
    completedTasks,
    completionRate,
    urgentTasks,
    vitalTasks,
    recentTasks,
    priorityTasks,
    
    // 方法
    getActivityData,
    getProductivityData,
    getProductivityInsights,
  };
}; 